class EstimatedDeliveryDate {
  // ─── Shared data layer (static) ─────────────────────────────────────────────
  static CACHE_KEY = "edd_shipping_data";
  static CACHE_ZIP = "edd_user_zip";
  static CACHE_CITY = "edd_user_city";
  static CACHE_TTL = 60 * 60 * 1000; // 1 hour in ms
  static API_BASE = "https://evhubcart.azurewebsites.net/api/carts/Shippings/";
  static GEO_API = "https://ipapi.co/json/";
  static VIACEP_API = "https://viacep.com.br/ws";
  static _dataPromise = null;
  static _instances = [];

  // ─── Instance ────────────────────────────────────────────────────────────────
  constructor(container) {
    this.target = container;
    EstimatedDeliveryDate._instances.push(this);
    this._init();
  }

  get shippingType() {
    return this.target.getAttribute("shipping_type") || "faster";
  }

  get dateMode() {
    return this.target.getAttribute("date-mode") || "shipping_days";
  }

  async render() {
    const data = await EstimatedDeliveryDate._getData();
    if (!data) return;

    const methods = data.data;
    const faster = methods.reduce((a, b) =>
      a.estimateBusinessDays <= b.estimateBusinessDays ? a : b,
    );
    const cheapest = methods.reduce((a, b) =>
      a.shippingPrice <= b.shippingPrice ? a : b,
    );

    if (this.shippingType === "full") {
      this._renderFullList(methods, faster.id, cheapest.id);
    } else {
      const method = this.shippingType === "faster" ? faster : cheapest;
      this.target.innerHTML = EstimatedDeliveryDate._buildDateText(
        method,
        this.dateMode,
      );
    }
  }

  _renderFullList(methods, fasterId, cheapestId) {
    const html = methods
      .map((method) => {
        const isFaster = method.id === fasterId;
        const isCheapest = method.id === cheapestId;
        const dateText = EstimatedDeliveryDate._buildDateText(
          method,
          this.dateMode,
        );
        const priceText =
          method.shippingPrice === 0
            ? "Grátis"
            : `R$ ${method.shippingPrice.toFixed(2).replace(".", ",")}`;

        const labels = [
          isFaster
            ? '<span class="edd-label edd-label--faster">Mais rápido</span>'
            : "",
          isCheapest
            ? '<span class="edd-label edd-label--cheapest">Mais barato</span>'
            : "",
        ]
          .filter(Boolean)
          .join("");

        const classes = [
          "edd-method",
          isFaster && "edd-method--faster",
          isCheapest && "edd-method--cheapest",
        ]
          .filter(Boolean)
          .join(" ");

        return `
        <div class="${classes}">
          <div class="edd-method__info">
            <span class="edd-method__title">${method.title}</span>
            ${labels ? `<div class="edd-method__labels">${labels}</div>` : ""}
          </div>
          <div class="edd-method__details">
            <span class="edd-method__date">${dateText}</span>
            <span class="edd-method__price">${priceText}</span>
          </div>
        </div>`;
      })
      .join("");

    this.target.innerHTML = `<div class="edd-list">${html}</div>`;
  }

  async setEvents() {
    document.addEventListener("cart:updated", () =>
      EstimatedDeliveryDate._clearCache(),
    );
    document.addEventListener("cart:change", () =>
      EstimatedDeliveryDate._clearCache(),
    );
  }

  async _init() {
    await this.setEvents();
    this.render();
  }

  // ─── Static: shared data fetching ───────────────────────────────────────────

  static _readCache() {
    try {
      const raw = sessionStorage.getItem(EstimatedDeliveryDate.CACHE_KEY);
      if (!raw) return null;
      const { payload, ts } = JSON.parse(raw);
      if (Date.now() - ts > EstimatedDeliveryDate.CACHE_TTL) {
        sessionStorage.removeItem(EstimatedDeliveryDate.CACHE_KEY);
        return null;
      }
      return payload;
    } catch (_) {
      return null;
    }
  }

  static _writeCache(data) {
    sessionStorage.setItem(
      EstimatedDeliveryDate.CACHE_KEY,
      JSON.stringify({ payload: data, ts: Date.now() }),
    );
  }

  static _clearCache() {
    sessionStorage.removeItem(EstimatedDeliveryDate.CACHE_KEY);
    EstimatedDeliveryDate._dataPromise = null;
  }

  static _getData() {
    if (!EstimatedDeliveryDate._dataPromise) {
      EstimatedDeliveryDate._dataPromise = EstimatedDeliveryDate._loadData();
    }
    return EstimatedDeliveryDate._dataPromise;
  }

  static async _loadData() {
    const cached = EstimatedDeliveryDate._readCache();
    if (cached) return cached;

    const zip = await EstimatedDeliveryDate._getUserZipCode();
    const cartToken = EstimatedDeliveryDate._getCartToken();
    const evolveToken = sessionStorage.getItem("EvolveToken");

    if (!evolveToken) {
      EvolveCore.Helpers.triggerEvent(`Cart:get`);
    }

    if (!zip || !cartToken || !evolveToken) return null;

    const data = await EstimatedDeliveryDate._fetchShippingData(
      zip,
      cartToken,
      evolveToken,
    );
    if (!data || !data.success || !data.data || !data.data.length) return null;

    EstimatedDeliveryDate._writeCache(data);
    return data;
  }

  static async _getUserZipCode() {
    const cached = sessionStorage.getItem(EstimatedDeliveryDate.CACHE_ZIP);
    if (cached) return cached;

    try {
      // Step 1: IP geolocation — get state (region_code) and city
      const geoRes = await EstimatedDeliveryDate._fetchWithTimeout(
        EstimatedDeliveryDate.GEO_API,
        {},
        4000,
      );
      if (!geoRes.ok) return null;

      const geo = await geoRes.json();
      const state = geo.region_code;
      const city = geo.city;
      if (!state || !city) return null;

      // Step 2: ViaCEP — resolve a real Brazilian zipcode from state + city
      const viacepUrl = `${EstimatedDeliveryDate.VIACEP_API}/${encodeURIComponent(state)}/${encodeURIComponent(city)}/Rua/json/`;
      const cepRes = await EstimatedDeliveryDate._fetchWithTimeout(
        viacepUrl,
        {},
        4000,
      );
      if (!cepRes.ok) return null;

      const cepData = await cepRes.json();
      if (!Array.isArray(cepData) || !cepData.length || !cepData[0]?.cep)
        return null;

      const zip = cepData[0].cep.replace(/\D/g, "");
      if (!zip) return null;

      sessionStorage.setItem(EstimatedDeliveryDate.CACHE_ZIP, zip);
      sessionStorage.setItem(EstimatedDeliveryDate.CACHE_CITY, city);
      return zip;
    } catch (_) {
      return null;
    }
  }

  static _getCartToken() {
    const match = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  static async _fetchShippingData(zip, cartToken, evolveToken) {
    const encoded = encodeURIComponent(cartToken);
    const res = await EstimatedDeliveryDate._fetchWithTimeout(
      EstimatedDeliveryDate.API_BASE + encoded,
      {
        method: "POST",
        headers: {
          Accept: "application/json, text/plain, */*",
          Authorization: `Bearer ${evolveToken}`,
          evhub_device: JSON.stringify({
            hash: null,
            name: "Mac OS X 10.15.7",
            browser: "Chrome 145",
          }),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ zipCode: zip }),
      },
      6000,
    );
    return res.json();
  }

  static _fetchWithTimeout(url, opts, ms) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("timeout")), ms);
      fetch(url, opts).then(
        (res) => {
          clearTimeout(timer);
          resolve(res);
        },
        (err) => {
          clearTimeout(timer);
          reject(err);
        },
      );
    });
  }

  static _buildDateText(method, dateMode) {
    const city = sessionStorage.getItem(EstimatedDeliveryDate.CACHE_CITY);
    const cityTag = city ? ` ` : "";
    // const cityTag = city ? ` <small>para ${city}</small>` : "";
    const iconTag = `
      <div class="edd-block-preview__icon">
        <img src="//simpleorganic.com.br/cdn/shop/t/344/assets/truck__icon.png?v=104254015566287837131777657267" alt="Ajuda">
      </div>
    `;

    if (dateMode === "calculated_date") {
      const date = EstimatedDeliveryDate._addBusinessDays(
        new Date(),
        method.estimateBusinessDays,
      );
      return `${iconTag} Receba ${method.shippingPrice === 0 ? `grátis` : ``} até ${EstimatedDeliveryDate._formatDate(date)}${cityTag}`;
    }
    return `${iconTag} Receba ${method.shippingPrice === 0 ? `grátis` : ``} em até ${method.estimateBusinessDays} dias úteis${cityTag}`;
  }

  static _addBusinessDays(date, days) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return result;
  }

  static _formatDate(date) {
    const months = [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ];
    return `${date.getDate()}/${months[date.getMonth()]}`;
  }

  static _notifyInstances(data) {
    sessionStorage.removeItem(EstimatedDeliveryDate.CACHE_CITY);
    EstimatedDeliveryDate._writeCache(data);
    EstimatedDeliveryDate._dataPromise = null;
    EstimatedDeliveryDate._instances.forEach((instance) => instance.render());
  }

  static _interceptFetch() {
    const _fetch = window.fetch.bind(window);
    window.fetch = async function (...args) {
      const res = await _fetch(...args);

      const url = args[0] instanceof Request ? args[0].url : String(args[0]);
      if (res.ok && url.startsWith(EstimatedDeliveryDate.API_BASE)) {
        res
          .clone()
          .json()
          .then((data) => {
            if (data?.success) EstimatedDeliveryDate._notifyInstances(data);
          })
          .catch(() => {});
      }

      return res;
    };
  }

  static _interceptXHR() {
    const _open = XMLHttpRequest.prototype.open;
    const _send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._eddUrl = String(url);
      return _open.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (this._eddUrl?.startsWith(EstimatedDeliveryDate.API_BASE)) {
        this.addEventListener("load", function () {
          if (this.status >= 200 && this.status < 300) {
            try {
              const data = JSON.parse(this.responseText);
              if (data?.success) EstimatedDeliveryDate._notifyInstances(data);
            } catch (_) {}
          }
        });
      }
      return _send.apply(this, args);
    };
  }
}

EstimatedDeliveryDate._interceptFetch();
EstimatedDeliveryDate._interceptXHR();

document
  .querySelectorAll("[estimated-delivery-date]")
  .forEach((el) => new EstimatedDeliveryDate(el));
